import type { StrictOmit } from '@enmon/commons';
import type {
  CountableApiResponse,
  MeterPopulate,
  SerializedCombinedMeter,
  SerializedCustomer,
  SerializedMeter,
} from '@enmon/commons/lib/enmon';
import type {
  BaseApiResponse,
  InventoryLocation,
  SerializedHardware,
  SerializedInventoryLocation,
  SerializedInventoryLocationBuilding,
  SerializedInventoryLocationBuildingFloorplan,
  SerializedInventoryMeter,
  SerializedInventoryMeterReadingGroup,
} from '@enmon/commons/lib/tools';
import { buildQuery, type PaginationByPage, type Populate } from '@enmon/commons/lib/tools/utils/buildQuery';
import type { StrapiApiResponse } from 'api/types/strapiApiResponse';
import { BASE_REST_API_URL } from 'common/constants';
import { parseSort } from 'lib/utils/parseSort';
import { isNil } from 'ramda';
import type { MeterFilter } from '../../../types/backendGateway/metersApi';
import type { BaseAPIParams } from '../../utils/BaseAPIParams';
import { convertSortToEnmonFormat } from '../../utils/convertSortToEnmonFormat';
import { BaseApiClient } from '../baseApiClient';
import type { MetersFilter as InventoryMetersFilter } from './metersApi';

export type InventoryLocationFilter = BaseAPIParams<SerializedInventoryLocation> & {
  backend_gateway_customer_ids?: string[];
  name?: string;
};

export type InventoryLocationMetersFilter = Partial<
  Pick<MeterFilter, 'search' | 'commodities' | 'toolsIds'> &
    Pick<InventoryMetersFilter, 'id' | 'buildings' | 'active' | 'submittedBy' | 'submittedAt' | 'sort'>
> &
  Partial<PaginationByPage & { enmonMeterPopulate: MeterPopulate[] }> & {
    readingGroups?: SerializedInventoryMeterReadingGroup['id'][];
    assignedHardwares?: SerializedHardware['id'][];
  };

export type InventoryLocationFloorplansFilter = Partial<Pick<InventoryMetersFilter, 'sort'>> & {
  inv_loc_building?: SerializedInventoryLocationBuilding['id'][];
};

export type GetInventoryLocationFloorplansArg = {
  locationId: InventoryLocation['id'];
  filter: InventoryLocationFloorplansFilter;
  pagination: { page: number; pageSize: number };
};

export type LocationFloorplan = SerializedInventoryLocationBuildingFloorplan & {
  meter_count: number;
};
export type GetInventoryLocationFloorplansResult = StrapiApiResponse<LocationFloorplan>;

export type InventoryLocationDto = StrictOmit<
  InventoryLocation,
  'id' | 'created_at' | 'updated_at' | 'avatar_url' | 'inventory_location_buildings' | 'inventory_meters'
>;

export type PopulatedInventoryLocation = SerializedInventoryLocation & {
  customer?: SerializedCustomer;
};

export type FindOneInventoryLocationResult = BaseApiResponse<SerializedInventoryLocation>;

export type FindInventoryLocationsArg = InventoryLocationFilter;
export type FindInventoryLocationsResult = StrapiApiResponse<PopulatedInventoryLocation>;

export type GetInventoryLocationMetersResult<T extends SerializedCombinedMeter = SerializedCombinedMeter> =
  CountableApiResponse<T[]>;

export type FindInventoryLocationsMetadataResult = StrapiApiResponse<SerializedInventoryLocation>;
export type CreateInventoryLocationArg = Partial<InventoryLocationDto>;
export type GenerateApiKeyArg = SerializedInventoryLocation['id'];
export type SyncInventoryLocationArg = {
  id: SerializedInventoryLocation['id'];
  inventoryLocationBuildingIds: Array<SerializedInventoryLocationBuilding['id']>;
};
export type SyncInventoryLocationResult = void;

export interface UpdateInventoryLocationArgs {
  id: string | number;
  values: Partial<InventoryLocationDto>;
}

export type LocationMetersSummary = {
  inventory_location_building: Array<{
    name: SerializedInventoryLocationBuilding['name'];
    count: number;
  }>;
  submitted_by: Array<{
    name: SerializedInventoryMeter['submitted_by'];
    count: number;
  }>;
  commodity: Array<{
    name: SerializedMeter['commodity'];
    count: number;
  }>;
};

export class LocationsApiClient extends BaseApiClient {
  constructor(jwt: string) {
    super('inventory-locations', BASE_REST_API_URL, jwt);
  }

  public async create(values: CreateInventoryLocationArg): Promise<SerializedInventoryLocation> {
    const response = await this.axios.post<SerializedInventoryLocation>('', { data: values });

    return response.data;
  }

  public async find(filter: FindInventoryLocationsArg = {}): Promise<FindInventoryLocationsResult> {
    const { backend_gateway_customer_ids, name, page, pageSize, sort, start, limit, fields } = filter;

    const response = await this.axios.get<FindInventoryLocationsResult>('', {
      params: buildQuery<SerializedInventoryLocation>({
        sort,
        pagination: {
          page,
          pageSize,
          start,
          limit,
        },
        filters: {
          ...(backend_gateway_customer_ids && { backend_gateway_customer_id: { $in: backend_gateway_customer_ids } }),
          ...(name && { name: { $containsi: name } }),
        },
        fields,
        populate: ['customer'] as Populate<SerializedInventoryLocation>,
      }),
    });

    return response.data;
  }

  public async findOne(id: string | number): Promise<FindOneInventoryLocationResult> {
    const response = await this.axios.get<FindOneInventoryLocationResult>(id.toString(), {
      params: buildQuery<SerializedInventoryLocation>({
        filters: {},
        populate: ['inventory_location_buildings'] as Populate<SerializedInventoryLocation>,
      }),
    });

    return response.data;
  }

  public async findMetadata(filter: InventoryLocationFilter = {}): Promise<FindInventoryLocationsMetadataResult> {
    const { backend_gateway_customer_ids, name, page, pageSize, sort } = filter;

    const response = await this.axios.get<FindInventoryLocationsMetadataResult>('metadata', {
      params: buildQuery({
        sort,
        pagination: {
          page,
          pageSize,
        },
        filters: {
          ...(backend_gateway_customer_ids && { backend_gateway_customer_id: { $in: backend_gateway_customer_ids } }),
          ...(name && { name: { $containsi: name } }),
        },
      }),
    });

    return response.data;
  }

  public async getFloorplans(
    { locationId, filter, pagination }: GetInventoryLocationFloorplansArg,
    signal?: AbortSignal,
  ): Promise<GetInventoryLocationFloorplansResult> {
    const { sort, inv_loc_building } = filter ?? {};

    const response = await this.axios.get<GetInventoryLocationFloorplansResult>(`${locationId}/floorplans`, {
      params: buildQuery<SerializedInventoryLocationBuildingFloorplan>({
        pagination,
        sort: parseSort(sort),
        filters: {
          ...(inv_loc_building && { inv_loc_building: { id: { $in: inv_loc_building } } }),
        },
      }),
      signal,
    });

    return response.data;
  }

  public async getMeters<T extends SerializedCombinedMeter = SerializedCombinedMeter>(
    locationId: SerializedInventoryLocation['id'],
    filter: InventoryLocationMetersFilter | undefined,
    signal: AbortSignal | undefined,
  ): Promise<GetInventoryLocationMetersResult<T>> {
    const { sort, submittedAt, active, ...restFilter } = filter ?? {};
    const sorts = convertSortToEnmonFormat(sort);

    const response = await this.axios.get<GetInventoryLocationMetersResult<T>>(`${locationId}/meters`, {
      params: {
        ...restFilter,
        // BE filter expects active not isActive
        ...(!isNil(active) && { active: Boolean(active) }),
        ...(submittedAt && { submittedAt: submittedAt.map(value => value.toISOString()) }),
        ...(sorts && { sorts: sorts.map(({ field, order }) => `${order === 'descend' ? '-' : ''}${field}`).join(',') }),
      },
      signal,
    });

    return response.data;
  }

  public async getMetersSummary(locationId: SerializedInventoryLocation['id']): Promise<LocationMetersSummary> {
    const response = await this.axios.get<LocationMetersSummary>(`${locationId}/meters-summary`);
    return response.data;
  }

  public async update({ id, values }: UpdateInventoryLocationArgs): Promise<SerializedInventoryLocation> {
    const response = await this.axios.put<SerializedInventoryLocation>(id.toString(), { data: values });
    return response.data;
  }

  public async generateApiKey(id: GenerateApiKeyArg): Promise<SerializedInventoryLocation> {
    const response = await this.axios.get<SerializedInventoryLocation>(`${id}/api-key/generate`);
    return response.data;
  }

  public async sync({
    id,
    inventoryLocationBuildingIds,
  }: SyncInventoryLocationArg): Promise<SyncInventoryLocationResult> {
    await this.axios.post<void>(`${id}/sync`, { inventoryLocationBuildingIds });
  }
}
